import React, { useEffect, useState } from 'react';
import {
  Alert,
  BottomNavigationAction,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  apiService,
  useGetPromotion,
  useUpdatePromotion,
} from 'src/services/apiService';
import { useRouter } from 'src/hooks/useRouter';
import { EntityTitle } from 'src/components/EntityTitle';
import { Close, Link, Save } from '@mui/icons-material';
import { BottomNavigation } from '../BottomNavigation';
import { Promotion } from 'src/types/api';
import { FileUpload } from 'src/components/common/FileUpload';
import { format } from 'date-fns';

import { BANNERS_REQUIREMENTS } from 'src/entities/promotion';

import { DateControl } from 'src/shared/ui/entity-control';
import { PUBLIC_PORTAL_HOST } from 'src/shared/config';


interface Props {}

export const PromotionPage: React.FunctionComponent<Props> = ({}) => {
  const router = useRouter();

  const [sizesDialog, setSizesDialog] = useState(false);

  const request = useGetPromotion({ promotionId: router.query.promotionId });

  const promotion = request?.data;

  const [state, changeState] = useState<
    Partial<
      Promotion & {
        bannerType?: string | null;
        bannerDisplayType?: string | null;
      }
    >
  >({});
  const [errors, setErrors] = useState<string[]>([]);

  const setState = (
    data: Partial<
      Promotion & {
        bannerType?: string | null;
        bannerDisplayType?: string | null;
      }
    >,
  ) => {
    if (errors.length) {
      setErrors([]);
    }
    changeState({ ...state, ...data });
  };

  useEffect(() => {
    if (request.data) {
      setState({
        bannerType: request.data?.bannerFull?.type,
        bannerDisplayType: request.data?.bannerFull?.displayType,
      });
    }
  }, [request.data]);

  const [update] = useUpdatePromotion();

  if (!request.data) return null;

  const requirements =
    state.bannerDisplayType &&
    BANNERS_REQUIREMENTS[state.bannerDisplayType]?.[state.bannerType as string];

  const handleUpdate = () => {
    let url;
    try {
      if (state.url) {
        url = new URL(state.url);
      }
    } catch {
      setErrors(errors => [...errors, 'Некорректный URL']);
    }

    if (state.url && url?.hostname !== 'instock-dv.ru') {
      setErrors(errors => [...errors, 'Некорректный домен URL']);
    }

    if (request.data?.id && !errors.length) {
      update({
        id: request.data.id,
        data: {
          ...state,
          start: state.start && format(new Date(state.start), 'yyyy-MM-dd'),
          finish: state.finish && format(new Date(state.finish), 'yyyy-MM-dd'),
        },
      }).then(request.refetch);
    }
  };

  const handleUploadImage = (files: File[], type: 'banner' | 'wobbler') => {
    if (!files[0] || !request.data?.id) return null;
    ({
      banner: apiService.v1ApiPrivate.promotionsUploadBanner,
      wobbler: apiService.v1ApiPrivate.promotionsUploadWobbler,
    })
      [type](request?.data?.id as string, { [type]: files[0] })
      .then(request.refetch);
  };

  const handleDeleteImage = (type: 'banner' | 'wobbler') => {
    if (!request.data?.id) return null;
    ({
      banner: apiService.v1ApiPrivate.promotionsBannerDelete,
      wobbler: apiService.v1ApiPrivate.promotionsWobblerDelete,
    })
      [type](request?.data?.id as string)
      .then(request.refetch);
  };

  return (
    <div>
      <EntityTitle id={request?.data?.id} title={request?.data?.title} />
      <Box mt={2}>
        <TextField
          label="vendoerId"
          variant="outlined"
          fullWidth
          disabled
          value={request?.data?.vendorId || ''}
        />
      </Box>
      <Box mt={2}>
        <TextField
          label="Оригинальное название"
          variant="outlined"
          fullWidth
          disabled
          value={request?.data?.vendorName || ''}
        />
      </Box>
      <Box mt={2}>
        <TextField
          label="Публичное название"
          variant="outlined"
          fullWidth
          value={(state?.title ?? request?.data?.title) || ''}
          onChange={e => setState({ title: e.target.value })}
        />
      </Box>
      <Box mt={2} maxWidth="220px">
        <DateControl
          editable
          label="Дата начала"
          value={(state?.start ?? request?.data?.start) || null}
          onChange={start => start && setState({ start })}
        />
      </Box>
      <Box mt={2} maxWidth="220px">
        <DateControl
          editable
          label="Дата окончания"
          value={(state?.finish ?? request?.data?.finish) || null}
          onChange={finish => finish && setState({ finish })}
        />
      </Box>
      <Box mt={2}>
        <TextField
          label="URL"
          variant="outlined"
          fullWidth
          value={(state?.url ?? request?.data?.url) || ''}
          onChange={e => setState({ url: e.target.value })}
          InputProps={{
            endAdornment: request?.data?.url && (
              <InputAdornment position="end">
                <IconButton href={request?.data?.url} target="_blank">
                  <Link />
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText="Пример: https://instock-dv.ru/brands/313"
        />
      </Box>
      {/* <Box mt={2}>
        <TextField
          label="Подпись"
          fullWidth
          variant="outlined"
          value={(state?.caption ?? request?.data?.caption) || ''}
          onChange={e => setState({ caption: e.target.value })}
        />
      </Box> */}
      <Box mt={2}>
        <TextField
          label="Описание"
          fullWidth
          variant="outlined"
          value={(state?.description ?? request?.data?.description) || ''}
          onChange={e => setState({ description: e.target.value })}
        />
      </Box>
      <Box mt={2}>
        <Button variant="contained" onClick={() => setSizesDialog(true)}>
          Размеры баннеров
        </Button>
        <Dialog
          open={sizesDialog}
          onClose={() => setSizesDialog(false)}
          maxWidth="xl"
        >
          <DialogContent>
            {Object.entries(BANNERS_REQUIREMENTS).map((item, index) => (
              <Box key={item[0]} mt={2}>
                <Box>
                  <Typography variant="h6">{item[0]}</Typography>
                </Box>

                <Box>
                  {Object.entries(item[1]).map(sizes => (
                    <Box key={sizes[0]}>
                      <Box>
                        {sizes[0]}: {sizes[1].width}x{sizes[1].height}px
                      </Box>
                      <Box>
                        <img
                          src={`${PUBLIC_PORTAL_HOST}/misc/banners_sizes/${item[0]}_${sizes[0]}.jpg`}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box></Box>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSizesDialog(false)}>закрыть</Button>
          </DialogActions>
        </Dialog>
      </Box>
      <Box mt={2}>
        <FileUpload
          lablel="Добавить банер"
          onChange={files => handleUploadImage(files, 'banner')}
        >
          {request?.data?.banner ? (
            <Box
              style={{
                padding: '0 24px',
                backgroundColor: request?.data?.backgroundColor || 'unset',
              }}
            >
              <img style={{ display: 'block' }} src={request?.data?.banner} />
            </Box>
          ) : null}
        </FileUpload>
      </Box>
      {Boolean(request?.data?.banner) && (
        <Box mt={2}>
          <Button
            variant="outlined"
            onClick={() => handleDeleteImage('banner')}
          >
            <Close />
            <Box ml={2}>Удалить банер</Box>
          </Button>
        </Box>
      )}
      {promotion?.bannerFull && (
        <>
          <Box mt={2}>
            <FormControl>
              <InputLabel id="displayType" shrink>
                Место отображения
              </InputLabel>
              <Select
                notched
                labelId="displayType"
                value={state.bannerDisplayType || ''}
                label="Место отображения"
                sx={{ minWidth: '200px' }}
                onChange={e =>
                  setState({
                    bannerDisplayType: e.target.value,
                    bannerType: null,
                  })
                }
              >
                {promotion?.bannerFull?.displayTypes
                  ?.filter(type => BANNERS_REQUIREMENTS[type.value])
                  ?.map(type => (
                    <MenuItem value={type.value} key={type.value}>
                      {type.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
          <Box mt={2}>
            <FormControl>
              <InputLabel id="type">Вариант отображения</InputLabel>
              <Select
                notched
                labelId="type"
                value={state.bannerType || ''}
                label="Вариант отображения"
                onChange={e => setState({ bannerType: e.target.value })}
                sx={{ minWidth: '200px' }}
              >
                {promotion?.bannerFull?.types
                  ?.filter(
                    type =>
                      state.bannerDisplayType &&
                      BANNERS_REQUIREMENTS[state.bannerDisplayType]?.[
                        type.value
                      ],
                  )
                  ?.map(type => (
                    <MenuItem value={type.value} key={type.value}>
                      {type.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}
      {requirements && (
        <Box mt={2}>
          <Box>Требования:</Box>
          <Box>
            {requirements.width}x{requirements.height}px
          </Box>
        </Box>
      )}
      {errors && (
        <Box>
          {errors.map(error => (
            <Box mt={1} key={error}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ))}
        </Box>
      )}
      <Box mt={8}>
        <BottomNavigation>
          <BottomNavigationAction
            label="Сохранить"
            icon={<Save />}
            onClick={handleUpdate}
          />
        </BottomNavigation>
      </Box>
    </div>
  );
};
